import { useState } from 'react';
import { Input } from 'components/ui';
import { clamp } from '../dashboardDraft';

interface DashboardNumberFieldProps {
  disabled?: boolean;
  // Whether an empty box is a real edit. An optional key (`fields.limit`) is removed when the box is
  // cleared; a required one (`position.w`, `layout.columns`) keeps its last value instead, because
  // the draft has no valid representation of "no width" and the re-flow would write 1 back anyway.
  isRequired?: boolean;
  max?: number;
  min?: number;
  onChange: (next: number | undefined) => void;
  testId?: string;
  value: number | undefined;
}

const format = (value: number | undefined): string => (value === undefined ? '' : String(value));

// The box shows what was typed, not what is stored. The draft still only ever receives a whole
// number inside [min, max] — so nothing here can produce a payload the validator would reject — but
// the clamping happens out of sight instead of rewriting digits under the cursor. That rewriting is
// what made the previous control unusable: clearing a width snapped it straight back to 1, and a
// second digit was clamped away before it could become part of a two-digit number.
const DashboardNumberField = ({
  disabled = false,
  isRequired = false,
  max,
  min,
  onChange,
  testId,
  value,
}: DashboardNumberFieldProps) => {
  // Null means "show the committed value"; a string means the user is mid-edit.
  const [typed, setTyped] = useState<string | null>(null);

  const handleChange = (raw: string) => {
    setTyped(raw);

    if (raw.trim() === '') {
      if (!isRequired) {
        onChange(undefined);
      }
      return;
    }

    const parsed = Number(raw);
    // A number input reports '' for a partial entry such as '-', so an unparseable string only
    // reaches here from a paste. Either way it is held on screen and nothing is committed.
    if (!Number.isFinite(parsed)) {
      return;
    }

    onChange(
      clamp(Math.round(parsed), min ?? Number.NEGATIVE_INFINITY, max ?? Number.POSITIVE_INFINITY)
    );
  };

  return (
    <Input
      disabled={disabled}
      // Leaving the field is what reconciles the two: whatever was actually stored comes back on
      // screen, so a clamped or rounded entry is visible rather than a silent surprise on save.
      onBlur={() => setTyped(null)}
      onChange={event => handleChange(event.target.value)}
      size="small"
      type="number"
      value={typed ?? format(value)}
      visualVariant="compactOutlined"
      // `inputProps` is Omitted from InputProps, so the bounds, the stepper and the test id all go
      // through slotProps (MUI 6). The bounds only drive the stepper — typed input is clamped above.
      slotProps={{ htmlInput: { 'data-testid': testId, max, min, step: 1 } }}
    />
  );
};

export default DashboardNumberField;
